import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import { LocalizedLink } from "@/i18n/language";
import { img } from "@/lib/images";

const hostsImg = img("about/about-gery-kompiang-01.jpg");

const headerImg = img("about/header-about-01.jpg");

export default function AboutPage() {
  const { t } = useTranslation();

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={t("nav_about")}
          title={t("about_heading")}
          subtitle={t("about_meet_subtitle")}
          image={headerImg}
          imageAlt="Bali VW Tour – Über uns – Reisfelder in Mengwi"
        />

        {/* Einführung */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="eyebrow-brand text-xs uppercase tracking-[0.35em] mb-3">
                — {t("about_title")} —
              </div>
              <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-[1.1] tracking-tight mb-6">
                {t("about_meet_title")}
              </h2>
              <p className="text-foreground-700 leading-relaxed text-base md:text-lg">
                {t("about_intro")}
              </p>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative rounded-lg overflow-hidden h-[420px] md:h-[640px]">
                <img
                  src={hostsImg}
                  alt={t("about_heading")}
                  title="Villa Kompiang – Ihre Gastgeber Kompiang & Gery"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Kompiang Wati – tief verwurzelt in Bali */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-3xl mb-8">
              <div className="eyebrow-brand text-xs uppercase tracking-[0.35em] mb-3">
                — {t("about_kompiang_story_eyebrow")} —
              </div>
              <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-[1.1] tracking-tight mb-8">
                {t("about_kompiang_story_title")}
              </h2>
              <p className="text-foreground-700 leading-relaxed text-base md:text-lg">
                {t("about_kompiang_roots_p2")}
              </p>
              <p className="mt-4 text-foreground-700 leading-relaxed text-base md:text-lg">
                {t("about_kompiang_roots_p3")}
              </p>
            </div>

            {/* Nostalgie-Collage: Kompiang Wati */}
            <div className="grid grid-cols-2 gap-3 md:gap-4 mb-8">
              <div className="rounded-lg overflow-hidden aspect-[3/4] group">
                <img
                  src={img("about/about-kompiang-01.jpg")}
                  alt="Kompiang Wati – Porträt aus den 2000er Jahren, Bali"
                  title="Kompiang Wati – frühe Jahre auf Bali"
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="rounded-lg overflow-hidden aspect-[3/4] group">
                <img
                  src={img("about/about-kompiang-02.jpg")}
                  alt="Kompiang Wati – Im Garten der Villa, Bali"
                  title="Kompiang Wati – bei der Arbeit auf der Villa"
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="rounded-lg overflow-hidden aspect-[3/4] group">
                <img
                  src={img("about/about-kompiang-03.jpg")}
                  alt="Kompiang Wati – Erinnerung aus dem Alltag auf Bali"
                  title="Kompiang Wati – Erinnerungen aus Bali"
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="rounded-lg overflow-hidden aspect-[3/4] group">
                <img
                  src={img("about/about-kompiang-04.jpg")}
                  alt="Kompiang Wati – Erinnerung aus dem Familienleben auf Bali"
                  title="Kompiang Wati – Erinnerungen aus Bali"
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              <div className="bg-background-100 border border-background-200 rounded-lg p-8 flex flex-col">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-5">
                  <i className="ri-music-2-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-xl text-foreground-950 mb-3">
                  {t("about_kompiang_voice_title")}
                </h3>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  {t("about_kompiang_voice_p1")}
                </p>
                <p className="mt-3 text-foreground-700 leading-relaxed text-sm">
                  {t("about_kompiang_voice_p2")}
                </p>
              </div>

              <div className="bg-background-100 border border-background-200 rounded-lg p-8 flex flex-col">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-5">
                  <i className="ri-restaurant-2-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-xl text-foreground-950 mb-3">
                  {t("about_kompiang_kitchen_title")}
                </h3>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  {t("about_kompiang_kitchen_p1")}
                </p>
                <p className="mt-3 text-foreground-700 leading-relaxed text-sm">
                  {t("about_kompiang_kitchen_p2")}
                </p>
              </div>

              <div className="bg-background-100 border border-background-200 rounded-lg p-8 flex flex-col">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-5">
                  <i className="ri-home-heart-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-xl text-foreground-950 mb-3">
                  {t("about_kompiang_family_title")}
                </h3>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  {t("about_kompiang_family_p1")}
                </p>
                <p className="mt-3 text-foreground-700 leading-relaxed text-sm">
                  {t("about_kompiang_family_p2")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Gery Nutz – von der Reiselust zur Lebensaufgabe */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-3xl mb-8">
              <div className="eyebrow-brand text-xs uppercase tracking-[0.35em] mb-3">
                — {t("about_gery_story_eyebrow")} —
              </div>
              <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-[1.1] tracking-tight mb-8">
                {t("about_gery_story_title")}
              </h2>
              <p className="text-foreground-700 leading-relaxed text-base md:text-lg">
                {t("about_gery_intro_p1")}
              </p>
              <p className="mt-4 text-foreground-700 leading-relaxed text-base md:text-lg">
                {t("about_gery_intro_p2")}
              </p>
              <p className="mt-4 text-foreground-700 leading-relaxed text-base md:text-lg">
                {t("about_gery_intro_p3")}
              </p>
            </div>

            {/* Nostalgie-Collage: Gery in den 1980ern */}
            <div className="grid grid-cols-2 gap-3 md:gap-4 mb-8">
              <div className="rounded-lg overflow-hidden aspect-[3/4] group">
                <img
                  src={img("about/about-gery-01.jpg")}
                  alt="Gery Nutz mit dem VW 181 in den Reisfeldern Balis – Bali Reisetour in den 1980ern"
                  title="Gery Nutz mit dem legendären VW 181 in den balinesischen Reisfeldern"
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="rounded-lg overflow-hidden aspect-[3/4] group">
                <img
                  src={img("about/about-gery-02.jpg")}
                  alt="Gery Nutz mit dem VW 181 am Straßenrand in Bali – Reisegruppe in den 1980ern"
                  title="Gery Nutz mit dem VW 181 und Reisegruppe auf Bali"
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="rounded-lg overflow-hidden aspect-[3/4] group">
                <img
                  src={img("about/about-gery-03.jpg")}
                  alt="Junger Gery Nutz mit balinesischen Tempelopfergaben – Bali in den 1980ern"
                  title="Gery Nutz lernt die balinesischen Traditionen kennen"
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="rounded-lg overflow-hidden aspect-[3/4] group">
                <img
                  src={img("about/about-gery-04.jpg")}
                  alt="Gery Nutz mit balinesischen Kindern auf einer Dorfstraße – Bali in den 1980ern"
                  title="Gery Nutz mit den Menschen Balis"
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              <div className="bg-background-50 border border-background-200 rounded-lg p-8 flex flex-col">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-5">
                  <i className="ri-compass-3-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-xl text-foreground-950 mb-3">
                  {t("about_gery_tour_title")}
                </h3>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  {t("about_gery_tour_p1")}
                </p>
                <p className="mt-3 text-foreground-700 leading-relaxed text-sm">
                  {t("about_gery_tour_p2")}
                </p>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-lg p-8 flex flex-col">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-5">
                  <i className="ri-lightbulb-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-xl text-foreground-950 mb-3">
                  {t("about_gery_understand_title")}
                </h3>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  {t("about_gery_understand_p1")}
                </p>
                <p className="mt-3 text-foreground-700 leading-relaxed text-sm">
                  {t("about_gery_understand_p2")}
                </p>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-lg p-8 flex flex-col">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-5">
                  <i className="ri-heart-3-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-xl text-foreground-950 mb-3">
                  {t("about_gery_love_title")}
                </h3>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  {t("about_gery_love_p1")}
                </p>
                <p className="mt-3 text-foreground-700 leading-relaxed text-sm">
                  {t("about_gery_love_p2")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Abschluss / CTA */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
            <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-[1.1] tracking-tight mb-6">
              {t("about_cta_title")}
            </h2>
            <p className="text-foreground-700 leading-relaxed text-base md:text-lg mb-10">
              {t("about_cta_text")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <LocalizedLink
                to="/contact"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-md font-semibold uppercase tracking-wider whitespace-nowrap cursor-pointer transition-colors text-sm"
              >
                {t("nav_contact")}
                <i className="ri-arrow-right-line"></i>
              </LocalizedLink>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}